import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './index.module.css';

const Tooltip = ({ content, type = 'info', size = 'default', openOnHover = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);
  // Opened by click/tap/keyboard: stays open after the mouse leaves, until clicked again or outside
  const isPinnedRef = useRef(false);

  const toggleTooltip = () => {
    isPinnedRef.current = !isPinnedRef.current;
    setIsOpen(isPinnedRef.current);
  };

  const closeTooltip = () => {
    isPinnedRef.current = false;
    setIsOpen(false);
  };

  // Mouse only: touch pointers enter and leave on every tap, so taps are left to the click toggle
  const handlePointerEnter = (event) => {
    if (!openOnHover || event.pointerType !== 'mouse') return;
    setIsOpen(true);
  };

  const handlePointerLeave = (event) => {
    if (!openOnHover || event.pointerType !== 'mouse' || isPinnedRef.current) return;
    setIsOpen(false);
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        closeTooltip();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const tooltipClasses = [
    'tooltip',
    isOpen && 'tooltip--open',
    size === 'large' && 'tooltip--large'
  ].filter(Boolean).join(' ');

  const buttonClasses = [
    'tooltip__button',
    type === 'question' && 'tooltip__button--question'
  ].filter(Boolean).join(' ');

  return (
    <div className={tooltipClasses} ref={tooltipRef} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
      <button
        type="button"
        className={buttonClasses}
        aria-expanded={isOpen}
        onClick={toggleTooltip}
      >
        <span className="visuallyhidden">More information</span>
      </button>
      <div role="tooltip" className="tooltip__content">
        {typeof content === 'string' ? <p>{content}</p> : content}
      </div>
    </div>
  );
};

Tooltip.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  type: PropTypes.oneOf(['info', 'question']),
  size: PropTypes.oneOf(['default', 'large']),
  openOnHover: PropTypes.bool
};

export default Tooltip;
